/* eslint-disable no-console */
import React, { createContext, useContext } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { AuthHelper } from '@testsa-com/common/src/helpers/Auth.helper'
import { getCookie } from 'cookies-next'
import { type Socket, io } from 'socket.io-client'

type SocketContextType = {
  socket?: Socket
}
const SocketContext = createContext<SocketContextType>({})

type NewChatMessagePayload = { projectId?: string | null; chatChannelId?: string }

const parseNewChatMessage = (rawMessage: unknown): NewChatMessagePayload => {
  if (typeof rawMessage !== 'string') {
    return {}
  }
  try {
    return JSON.parse(rawMessage) as NewChatMessagePayload
  } catch {
    return {}
  }
}

export const useSocketContext = () => useContext(SocketContext)

let standaloneSocket:
  | {
      socket: Socket
      jwtToken: string
    }
  | undefined

export const SocketContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const queryClient = useQueryClient()

  const socket = React.useMemo(() => {
    const jwtToken = getCookie(AuthHelper.AUTH_COOKIE_NAME)
    if (jwtToken && standaloneSocket?.jwtToken === jwtToken) {
      return standaloneSocket.socket
    }
    if (standaloneSocket && standaloneSocket.jwtToken !== jwtToken) {
      standaloneSocket.socket.disconnect()
      standaloneSocket = undefined
    }
    if (!jwtToken || jwtToken === true) {
      return undefined
    }
    console.log('SOCKET connecting')
    const _socket = io(
      `${process.env.NEXT_PUBLIC_API_URL?.replace('https://', 'wss://')
        .replace('http://', 'ws://')
        // remove trailing slash
        .replace(/\/$/, '')}/socket/v1/chat`,
      {
        auth: { jwtToken },
        autoConnect: true,
        transports: ['websocket'],
      },
    )
    _socket.on('connect', () => {
      console.log('SOCKET connected')
    })

    _socket.on('disconnect', reason => {
      console.log('SOCKET disconnected', reason)
    })

    _socket.on('connect_error', err => {
      console.log('SOCKET connect_error', err)
    })

    standaloneSocket = {
      socket: _socket,
      jwtToken,
    }

    _socket.on('new-chat-message', (rawMessage: unknown) => {
      const { projectId, chatChannelId } = parseNewChatMessage(rawMessage)
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || typeof queryKey[0] !== 'string') {
            return false
          }
          // channels without a project are listed on every project
          if (!projectId || !chatChannelId) {
            return queryKey[0].startsWith('/api/v1/chat/')
          }
          return (
            queryKey[0].startsWith(`/api/v1/chat/projects/${projectId}/`) ||
            queryKey[0].startsWith(`/api/v1/chat/channels/${chatChannelId}/`)
          )
        },
      })
    })

    return _socket
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>
}
